import { useCallback, useMemo, useState, type ReactElement } from 'react'
import {
  type CalendarGenerateInput,
  getCanvassGenerateRecordCount,
  isEmpty,
  isFakeCompany,
  isNotEmpty,
  MAX_CANVASS_DEMO_GENERATE_RECORDS,
  type AccountStatusId,
  type CanvassGenerateInput,
  type DispositionId,
  type UserId,
  type WorkflowActionId,
  type WorkflowStageId,
} from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { Group, Text } from '@mantine/core'
import { useSelectedWorkflow, useToastError } from '@hooks'
import {
  useGenerateCalendarDemo,
  useGenerateCanvassDemo,
  useLoadedEntitlement,
  usePreference,
  useProfile,
} from '@front/model'
import { Page, TButton, TSegmentedControl } from '@components'
import {
  CalendarTool,
  CanvassTool,
  DEFAULT_SHOTGUN_BOUNDS,
  DemoToolsAccessDenied,
  type BoundingBox,
} from './components'
import { toCanvassGenerationNotification } from './canvassGenerationNotification'

type DemoToolMode = 'canvass' | 'calendar'

const DEFAULT_CALENDAR_MEETING_COUNT = 5

const DEMO_TOOL_OPTIONS = [
  { label: 'Canvass data', value: 'canvass' },
  { label: 'Calendar data', value: 'calendar' },
] satisfies { label: string; value: DemoToolMode }[]

function DemoToolsRoute(): ReactElement {
  const { company } = useProfile()
  const [activeTool, setActiveTool] = useState<DemoToolMode>('canvass')
  const [setterIds, setSetterIds] = useState<UserId[]>([])
  const [closerIds, setCloserIds] = useState<UserId[]>([])
  const [calendarUserIds, setCalendarUserIds] = useState<UserId[]>([])
  const [calendarMeetingCount, setCalendarMeetingCount] = useState(DEFAULT_CALENDAR_MEETING_COUNT)
  const [selectedDispositions, setSelectedDispositions] = useState<Map<DispositionId, number>>(new Map())
  const [selectedStages, setSelectedStages] = useState<Map<AccountStatusId, number>>(new Map())
  const [selectedWorkflowActions, setSelectedWorkflowActions] = useState<Map<WorkflowActionId, number>>(new Map())
  const [selectedWorkflowStages, setSelectedWorkflowStages] = useState<Map<WorkflowStageId, number>>(new Map())
  const [generationBounds, setGenerationBounds] = useState<BoundingBox>(DEFAULT_SHOTGUN_BOUNDS)
  const isRecruiting = useLoadedEntitlement('recruiting')
  const { data: closerInteraction } = usePreference('closerInteraction')
  const { workflowId, shouldUseWorkflow } = useSelectedWorkflow()

  const { add: generateCanvass, isAdding: isAddingCanvass, error: generateCanvassError } = useGenerateCanvassDemo()
  useToastError(generateCanvassError)

  const { add: generateCalendar, isAdding: isAddingCalendar, error: generateCalendarError } = useGenerateCalendarDemo()
  useToastError(generateCalendarError)

  const dispositionRequests = useMemo(
    () =>
      isRecruiting || shouldUseWorkflow
        ? []
        : Array.from(selectedDispositions.entries()).map(([dispositionId, count]) => ({
            dispositionId,
            count,
          })),
    [isRecruiting, selectedDispositions, shouldUseWorkflow]
  )

  const stageRequests = useMemo(
    () =>
      shouldUseWorkflow
        ? []
        : Array.from(selectedStages.entries()).map(([accountStageId, count]) => ({
            accountStageId,
            count,
          })),
    [selectedStages, shouldUseWorkflow]
  )

  const workflowStageRequests = useMemo(
    () =>
      shouldUseWorkflow
        ? Array.from(selectedWorkflowStages.entries()).map(([stageId, count]) => ({
            stageId,
            count,
          }))
        : [],
    [selectedWorkflowStages, shouldUseWorkflow]
  )

  const workflowActionRequests = useMemo(
    () =>
      shouldUseWorkflow
        ? Array.from(selectedWorkflowActions.entries()).map(([actionId, count]) => ({
            actionId,
            count,
          }))
        : [],
    [selectedWorkflowActions, shouldUseWorkflow]
  )

  const recordCount = useMemo(
    () =>
      getCanvassGenerateRecordCount({
        workflowId: shouldUseWorkflow ? workflowId : undefined,
        dispositions: dispositionRequests,
        stages: stageRequests,
        workflowStages: workflowStageRequests,
        workflowActions: workflowActionRequests,
      }),
    [dispositionRequests, shouldUseWorkflow, stageRequests, workflowActionRequests, workflowId, workflowStageRequests]
  )

  const validationMessages = useMemo(() => {
    const messages: string[] = []

    if (recordCount.total === 0) {
      messages.push('Add at least one account stage, workflow stage, workflow action, or disposition count.')
    }

    if (recordCount.total > MAX_CANVASS_DEMO_GENERATE_RECORDS) {
      messages.push(
        `Requested ${recordCount.total} records. Reduce by ${recordCount.total - MAX_CANVASS_DEMO_GENERATE_RECORDS} before generating.`
      )
    }

    if (recordCount.contacts > 0 && isEmpty(setterIds)) {
      messages.push('Pick at least one owner before generating contacts.')
    }

    if (shouldUseWorkflow && isEmpty(workflowId)) {
      messages.push('Select a workflow before generating workflow demo data.')
    }

    return messages
  }, [recordCount, setterIds, shouldUseWorkflow, workflowId])

  const resetCanvassSelections = useCallback((): void => {
    setSetterIds([])
    setCloserIds([])
    setSelectedDispositions(new Map())
    setSelectedStages(new Map())
    setSelectedWorkflowActions(new Map())
    setSelectedWorkflowStages(new Map())
  }, [])

  const resetCanvass = useCallback((): void => {
    resetCanvassSelections()
    setGenerationBounds(DEFAULT_SHOTGUN_BOUNDS)
  }, [resetCanvassSelections])

  const handleGenerateCanvass = useCallback(async (): Promise<void> => {
    if (isNotEmpty(validationMessages)) {
      notifications.show({
        title: 'Cannot generate demo data',
        message: validationMessages[0],
        color: 'red',
      })
      return
    }

    const input: CanvassGenerateInput = {
      boundingBox: isRecruiting ? undefined : generationBounds,
      setters: setterIds,
      closers: closerIds,
      workflowId: shouldUseWorkflow ? workflowId : undefined,
      workflowStages: workflowStageRequests,
      workflowActions: workflowActionRequests,
      dispositions: dispositionRequests,
      stages: stageRequests,
    }

    const result = await generateCanvass(input)
    notifications.show(toCanvassGenerationNotification({ result, requested: recordCount }))
    resetCanvassSelections()
  }, [
    closerIds,
    dispositionRequests,
    generateCanvass,
    generationBounds,
    isRecruiting,
    recordCount,
    setterIds,
    shouldUseWorkflow,
    stageRequests,
    validationMessages,
    workflowActionRequests,
    workflowId,
    workflowStageRequests,
    resetCanvassSelections,
  ])

  const handleGenerateCalendar = useCallback(async (): Promise<void> => {
    const input: CalendarGenerateInput = {
      meetingCount: calendarMeetingCount,
      userIds: isEmpty(calendarUserIds) ? undefined : calendarUserIds,
    }

    const data = await generateCalendar(input)
    notifications.show({
      title: 'Calendar data generated successfully',
      message: `Created ${data.meetingAttendanceGenerated} attendance records, ${data.interviewGenerated} interviews, and ${data.shadowGenerated} shadows`,
      color: 'green',
    })
  }, [calendarMeetingCount, calendarUserIds, generateCalendar])
  const resetCalendar = useCallback((): void => {
    setCalendarUserIds([])
    setCalendarMeetingCount(DEFAULT_CALENDAR_MEETING_COUNT)
  }, [])

  const handleReset = useCallback((): void => {
    if (activeTool === 'calendar') {
      resetCalendar()
      return
    }

    resetCanvass()
  }, [activeTool, resetCalendar, resetCanvass])

  const isAllowed = isFakeCompany(company)
  const hideCloser = closerInteraction ? closerInteraction === 'None' : true
  const isGeneratingActiveTool = activeTool === 'calendar' ? isAddingCalendar : isAddingCanvass

  if (!isAllowed) return <DemoToolsAccessDenied />

  return (
    <Page>
      <Page.Heading title='Demo Data Generator' />
      <Page.FunctionBar>
        <Group justify='space-between' align='center' w='100%'>
          <Group gap='sm'>
            <TSegmentedControl options={DEMO_TOOL_OPTIONS} value={activeTool} onChange={setActiveTool} />
            {activeTool === 'canvass' ? (
              <Text className='bold'>
                {recordCount.total} / {MAX_CANVASS_DEMO_GENERATE_RECORDS} records
              </Text>
            ) : (
              <Text className='bold'>
                {isEmpty(calendarUserIds) ? 'All users' : `${calendarUserIds.length} users`} · {calendarMeetingCount}{' '}
                meetings/user
              </Text>
            )}
          </Group>
          <TButton variant='ghost' onClick={handleReset} disabled={isGeneratingActiveTool}>
            Reset {activeTool === 'calendar' ? 'Calendar' : 'Canvass'}
          </TButton>
        </Group>
      </Page.FunctionBar>
      <Page.Content scrollArea>
        {activeTool === 'canvass' ? (
          <CanvassTool
            canGenerate={isEmpty(validationMessages)}
            closerIds={closerIds}
            generationBounds={generationBounds}
            hideCloser={hideCloser}
            isAddingCanvass={isAddingCanvass}
            isRecruiting={isRecruiting}
            onGenerate={handleGenerateCanvass}
            recordCount={recordCount}
            selectedDispositions={selectedDispositions}
            selectedStages={selectedStages}
            selectedWorkflowActions={selectedWorkflowActions}
            selectedWorkflowStages={selectedWorkflowStages}
            setCloserIds={setCloserIds}
            setGenerationBounds={setGenerationBounds}
            setSelectedDispositions={setSelectedDispositions}
            setSelectedStages={setSelectedStages}
            setSelectedWorkflowActions={setSelectedWorkflowActions}
            setSelectedWorkflowStages={setSelectedWorkflowStages}
            setSetterIds={setSetterIds}
            setterIds={setterIds}
            shouldUseWorkflow={shouldUseWorkflow}
            validationMessages={validationMessages}
            workflowActionCount={workflowActionRequests.reduce((sum, action) => sum + action.count, 0)}
            workflowId={workflowId}
          />
        ) : (
          <CalendarTool
            calendarMeetingCount={calendarMeetingCount}
            calendarUserIds={calendarUserIds}
            isAddingCalendar={isAddingCalendar}
            onGenerate={handleGenerateCalendar}
            setCalendarMeetingCount={setCalendarMeetingCount}
            setCalendarUserIds={setCalendarUserIds}
          />
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/demo-tools/')({
  component: DemoToolsRoute,
})
